import type { ApplicationInsights, ITelemetryItem } from '@microsoft/applicationinsights-web';
import type { EventName } from './observability';
import { queryOnlyPageView, scrubTelemetryUrls } from './lib/telemetryScrub';
import { APP_ENV } from './utils/appEnv';

// ── Layer ───────────────────────────────────────────────────────────────────
// This file is the TRANSPORT, the bottom of the frontend observability stack.
// It imports no other module of the app, except `lib/telemetryScrub`, which
// imports nothing.
//
//   observability.ts   the DOOR — components call record()
//        └── this file   the SINK — Application Insights
//
// Application code calls `record()` in `./observability`, NOT `trackEvent()`
// here (#382). `trackEvent` takes a typed name so a call that reaches it
// directly still cannot invent an event.
//
// These events land in the SAME Log Analytics workspace as the backend ones;
// `AppRoleName` separates them (`lmfapp-frontend-prod`).

// The browser's OWN Application Insights component since #421: this string
// is public by design, so it must not be able to write what the audit page
// reads, and the split is what makes that so.
const connectionString = import.meta.env.VITE_APPINSIGHTS_CONNECTION_STRING;

let appInsights: ApplicationInsights | null = null;

/** The signed-in user, or null. ONE initializer reads it (#452): `setUser()`
 *  added a new initializer on every call — each page load, each profile
 *  save — and `clearUser()` removed none, so after a sign-out the next
 *  person's telemetry carried the roles of the previous one. */
let currentUser: { id: string; roles: string[] } | null = null;

/** The path of the last page view that went out (queryOnlyPageView). */
let lastPagePath: string | null = null;

/** The SDK's default list, plus our CSRF header. `enableRequestHeaderTracking`
 *  copies every request header into the dependency row, and the SDK ignores
 *  only the first three names by default, so the CSRF token of every change
 *  went into `AppDependencies` (#452). The match ignores case. */
const IGNORED_HEADERS = ['Authorization', 'X-API-Key', 'WWW-Authenticate', 'X-CSRF-Token'];

if (connectionString) {
  import('@microsoft/applicationinsights-web').then(({ ApplicationInsights: AI }) => {
    appInsights = new AI({
      config: {
        connectionString,
        enableAutoRouteTracking: true,
        // On (#679). The SDK default is off, so a promise that rejects with no
        // catch left no row in Application Insights. ToastProvider still shows
        // the error of a rejected request to the user. The SDK records the same
        // rejection as an exception, and the initializer below scrubs it.
        //
        // A 5xx answer that no code catches gives two rows for one failure: the
        // interceptor of api/client.ts calls trackException too. Only the row of
        // the interceptor has the property `status`. The code keeps both rows.
        enableUnhandledPromiseRejectionTracking: true,
        // Off (#602 review): the SDK sent a PageVisitTime metric with each
        // page view, also with one that the initializer drops, so each key in
        // a search box sent a metric. It also kept the raw URL, with the
        // search text, in sessionStorage. No saved query reads the metric.
        autoTrackPageVisitTime: false,
        // SDK 3.4.5 sends its own "SDK Stats" when the remote config of the SDK
        // turns them on. They skip the URL scrub of this file, so they stay off
        // (security review, 2026-10-15). 2 is FeatureOptInMode.disable.
        featureOptIn: { sdkStats: { mode: 2 } },
        enableCorsCorrelation: true,
        // Cross-origin: SDK only adds traceparent / Request-Id / Request-Context
        // to XHRs whose host matches one of these. Without this, app.muaythai.lt →
        // api.muaythai.lt fetches go out without correlation headers and AI's
        // transaction-diagnostics view can't link browser → backend spans. Server
        // side mirrors via `Access-Control-Allow-Headers` in backend/src/index.ts.
        correlationHeaderDomains: ['api.muaythai.lt', 'api-test.muaythai.lt'],
        enableRequestHeaderTracking: true,
        enableResponseHeaderTracking: true,
        disableFetchTracking: false,
        enableAjaxPerfTracking: true,
        // The ajax plugin's own settings; its identifier is fixed in the SDK.
        extensionConfig: { AjaxDependencyPlugin: { ignoreHeaders: IGNORED_HEADERS } },
      },
    });
    // Stamp every envelope with cloud role + release version. Same AI
    // connection string is used for staging + prod, so cloud role is the
    // only signal that separates them. `lmfapp-frontend-local` fallback
    // marks envelopes from `npm run dev` (no VITE_ENV baked in).
    const env = APP_ENV;
    const version = import.meta.env.VITE_APP_VERSION;
    const cloudRole =
      env === 'production' ? 'lmfapp-frontend' :
      env === 'staging'    ? 'lmfapp-frontend-staging' :
                             'lmfapp-frontend-local';
    appInsights.addTelemetryInitializer((item: ITelemetryItem) => {
      // #595 review — a change of the query only (a filter, a tab, a key in
      // a search box) is no new page: the item does not go out.
      const pageView = queryOnlyPageView(item, lastPagePath);
      lastPagePath = pageView.lastPath;
      if (pageView.drop) return false;
      item.tags = item.tags || {};
      item.tags['ai.cloud.role'] = cloudRole;
      if (version) item.tags['ai.application.ver'] = version;
      if (currentUser?.roles.length) {
        item.data = item.data || {};
        item.data.roles = currentUser.roles.join(',');
      }
      scrubTelemetryUrls(item);
    });
    appInsights.loadAppInsights();
    // A sign-in can finish before this dynamic import does.
    if (currentUser) appInsights.setAuthenticatedUserContext(currentUser.id, undefined, true);
    // Signal SDK readiness so e2e tests can wait for window.fetch to be patched
    // before firing requests they expect to carry traceparent headers.
    (window as unknown as { __appInsightsReady?: boolean }).__appInsightsReady = true;
  }).catch(() => { /* package not installed — skip silently */ });
}

/** Set authenticated user context (call after login) */
export function setUser(userId: string, roles?: string[]) {
  currentUser = { id: userId, roles: roles ?? [] };
  appInsights?.setAuthenticatedUserContext(userId, undefined, true);
}

/** Clear user context (call on logout) */
export function clearUser() {
  currentUser = null;
  appInsights?.clearAuthenticatedUserContext();
}

/** Track a custom event. Prefer `record()` in ./observability — it adds the
 *  ambient properties every event carries. The name is typed either way, so
 *  a typo is a build error. The type is imported for its NAMES only; the
 *  value side of the catalogue lives in observability.ts and importing it
 *  here would make a cycle. */
export function trackEvent(name: EventName, properties?: Record<string, string>) {
  appInsights?.trackEvent({ name }, properties);
}

/** Track an exception */
export function trackException(error: Error, properties?: Record<string, string>) {
  appInsights?.trackException({ exception: error }, properties);
}
